import {
  type RouteLocationRaw,
  type RouteLocationNormalizedLoaded,
} from 'vue-router'
import { type Pinia } from 'pinia'
import { Cookies, Notify } from 'quasar'
import { type QSsrContext } from '#q-app'
import { i18n } from 'boot/common/i18n'

import { useAuthStore } from 'stores/receiver/account/auth/auth'
import { useUserStore } from 'stores/receiver/account/user/user'
import { useConfigStore } from 'stores/receiver/config'
import { useFeatureFlagStore } from 'stores/common/featureFlag'

import { useCheckRoles } from 'composables/receiver/user/useCheckRoles'

/**
 * Performs pre-fetching and redirection logic based on the provided route and store.
 *
 * @param route - The route object containing information about the current route.
 * @param store - The Pinia store instance.
 * @param redirect - The function used to redirect to a different route.
 * @param ssrContext - The server-side rendering (SSR) context, if available.
 * @returns A Promise that resolves when the pre-fetching and redirection logic is complete.
 */
export const usePrefetchRedirect = async (
  route: RouteLocationNormalizedLoaded,
  store: Pinia,
  redirect: (url: RouteLocationRaw, statusCode?: number) => void,
  ssrContext?: QSsrContext | null
) => {
  const authStore = useAuthStore(store)
  const userStore = useUserStore(store)
  const configStore = useConfigStore(store)
  const { isActive } = useFeatureFlagStore(store)

  // On logout: need to pay attention to save the store states if needed
  // Fetch public keys
  await configStore.fetchReceiverConfig()

  const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies // otherwise we're on client
  // Fetch user if we have access_token
  // Otherwise if we have refresh_token get a new access_token and fetch user
  if (!userStore.user && cookies.has('access_token')) {
    await userStore.fetchUserInfo()
  } else if (cookies.has('refresh_token')) {
    const res = await authStore.refreshTokens(ssrContext)

    if (res) {
      await userStore.fetchUserInfo()
    }
  }

  // Redirect to login if user is not logged in and page requires auth
  if (route.meta.requiresAuth && !userStore.isLoggedIn) {
    redirect({ path: '/login', query: cleanQueries() })
  } else if (route.meta.requiresAdmin && !userStore.isAdmin) {
    // Redirect to home page if user is not admin and try to access an admin page
    redirect({ path: '/', query: cleanQueries() })
  } else if (
    (route.meta.roles && !useCheckRoles().checkRoles(route.meta.roles)) ||
    (route.meta.oneOfRoles &&
      !useCheckRoles().checkRoles(route.meta.oneOfRoles, false))
  ) {
    // Check if user has Roles to access the page
    redirect({ path: '/', query: cleanQueries() })
    Notify.create({
      color: 'negative',
      message: i18n.global.t('error.auth.roles'),
    })
  } else if (route.meta.flag && !isActive(route.meta.flag)) {
    // Check if feature flag is active
    redirect({ path: '/', query: cleanQueries() })
    Notify.create({
      color: 'warning',
      message: i18n.global.t('error.auth.featureFlag'),
    })
  } else if (userStore.isLoggedIn && route.path === '/login') {
    // Redirect to home page if user is logged in and try to access login page
    redirect({ path: '/', query: cleanQueries() })
  }

  // Redirect to profile page if user is logged in and has not completed the profile
  if (
    userStore.isLoggedIn &&
    (!userStore.user?.profile.job ||
      !userStore.user?.profile.firstname ||
      !userStore.user?.profile.lastname) &&
    route.path !== '/sign-up/profile'
  ) {
    redirect({ path: '/sign-up/profile' })
    return
  } else if (
    process.env.FREE_PLAN !== '1' &&
    userStore.isLoggedIn &&
    route.meta.requiresAuth &&
    !userStore.subscription?.subscription_id &&
    route.path !== '/pricing/plans'
  ) {
    // Redirect to plans page if user is logged in and has not selected a plan
    redirect({ path: '/pricing/plans', query: cleanQueries() })
    return
  }
  // Redirect the templates to finish the template draft
  if (
    userStore.isLoggedIn &&
    cookies.has('savedDraft') &&
    route.path !== '/templates'
  ) {
    redirect({ path: '/templates', query: cleanQueries() })
    return
  }

  function cleanQueries() {
    // Only keep "error" query
    if (route.query.error) {
      return {
        error: route.query.error,
      }
    } else return undefined
  }
}
